import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Paper, Grid, Typography, Tooltip, IconButton } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import Filter from 'components/Filter/components';
import FilterMobile from 'components/FilterMobile/components';
import MTable from 'components/MTable';
import TableMobile from 'components/TableMobile';
import SellOrder from 'components/SellOrder/components';
import Resume from './Resume/Resume';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';

import { loadFilters } from 'components/Filter/store/thunk';
import { loadSellOrdersList, loadSellOrdersResume, loadSellOrdersExport, loadSellOrder } from '../store/thunk';

import { nameInput, documentInput, orderInput, productInput } from './resources';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import useDidMount from 'helpers/hooks/useDidMount';

const headers = [
  { title: 'Código', field: 'orderCode' },
  { title: 'Criação', field: 'registrationDateTime' },
  { title: 'Cliente', field: 'client' },
  { title: 'Meio de Pagamento', field: 'paymentMethodName' },
  { title: 'Valor Total', field: 'totalAmount' },
  { title: 'Situação', field: 'situationDescription' }
];

export default function SellOrders() {
  const dispatch = useDispatch();

  const didComponentMount = useDidMount();

  const { width } = useWindowDimensions();
  const isMobile = width < 1024;

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { startDate, endDate } = useSelector(state => state.dateFilter);
  const { qp, selects } = useSelector(state => state.filters);
  const { isLoading, total, list, resume } = useSelector(state => state.sellOrders);

  const [isSellOrderModalOpen, setIsSellOrderModalOpen] = useState(false);

  const [sellOrdersPage, setSellOrdersPage] = useState(1);
  const sellOrdersPageSize = 10;
  const sellOrdersPageCount = Math.ceil(total / sellOrdersPageSize);

  const handleLoadSellOrdersExport = () => {
    const extraProps = {
      startDate,
      endDate,
      qp
    }

    return dispatch(loadSellOrdersExport(userId, establishmentId, extraProps));
  }

  const handleLoadSellOrdersList = ({ page, qp }) => {
    const extraProps = {
      startDate,
      endDate,
      qp,
      page
    }

    dispatch(loadSellOrdersList(userId, establishmentId, extraProps));
  }

  const handleLoadSellOrdersResume = qp => {
    const extraProps = {
      startDate,
      endDate,
      qp
    }

    dispatch(loadSellOrdersResume(userId, establishmentId, extraProps));
  }

  useEffect(() => {
    if(establishmentId) {
      dispatch(loadFilters(userId, establishmentId));
      handleLoadSellOrdersResume(qp);
      handleLoadSellOrdersList({ page: 1, qp });
    }
  }, [establishmentId]);

  useEffect(() => {
    if(!didComponentMount) {
      handleLoadSellOrdersResume(qp);
      handleLoadSellOrdersList({ page: sellOrdersPage, qp });
    }
  }, [sellOrdersPage]);

  const formattedFilters = [
    nameInput,
    documentInput,
    {
      advanced: false,
      type: 'select',
      id: 'situationId',
      label: 'Situação',
      value: 0,
      options: selects
        ?.find(select => select?.id == 'orderSituation')?.options
        ?.map(option => {
          return {
            value: option?.id,
            label: option?.label
          }
        })
    },
    {
      advanced: false,
      type: 'select',
      id: 'registrationUserId',
      label: 'Usuário',
      value: 0,
      options: selects?.find(select => select?.id == 'user')?.options || []
    },
    orderInput,
    productInput,
    {
      advanced: false,
      type: 'select',
      id: 'paymentMethodId',
      label: 'Formas de Pagamento',
      value: 0,
      options: selects?.find(select => select?.id == 'paymentMethod')?.options || []
    },
  ];

  return(
    <>
      <Resume
        resume={resume}
        isMobile={isMobile}
        isLoading={isLoading}
      />
      <Paper>
        {isSellOrderModalOpen && (
          <SellOrder
            isSellOrderModalOpen={isSellOrderModalOpen}
            setIsSellOrderModalOpen={setIsSellOrderModalOpen}
            sellOrdersPageSize={sellOrdersPageSize}
            sellOrdersPage={sellOrdersPage}
            establishmentId={establishmentId}
            pageOrigin='/historico-de-venda'
          />
        )}
        {isMobile ? (
          <FilterMobile
            filter
            filters={formattedFilters}
            isDisabledPdfExport
            alternativeExport
            hasFilterButton
            functionAlternativeExport={handleLoadSellOrdersExport}
            exportType='sellOrders'
            fileName='Histórico de Venda'
            handlePageRequest={({ page, qp }) => {
              setSellOrdersPage(1);
              handleLoadSellOrdersResume(qp);
              handleLoadSellOrdersList({ page, qp });
            }}
          />
        ) : (
          <Filter
            filter
            filters={formattedFilters}
            isDisabledPdfExport
            alternativeExport
            functionAlternativeExport={handleLoadSellOrdersExport}
            exportType='sellOrders'
            fileName='Histórico de Venda'
            handlePageRequest={({ page, qp }) => {
              setSellOrdersPage(1);
              handleLoadSellOrdersResume(qp);
              handleLoadSellOrdersList({ page, qp });
            }}
          />
        )}
        {isMobile && (
          <TableMobile
            loading={isLoading}
            data={list?.map(item => {
              return {
                ...item,
                orderCode: {
                  index: 1,
                  label: 'Código',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.orderCode}</Typography>
                },
                registrationDateTime: {
                  index: 2,
                  label: 'Criação',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.registrationDateTime}</Typography>
                },
                client: {
                  index: 3,
                  label: 'Cliente',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.client}</Typography>
                },
                paymentMethodName: {
                  index: 4,
                  label: 'Meio de Pagamento',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.paymentMethodName}</Typography>
                },
                totalAmount: {
                  index: 5,
                  label: 'Valor Total',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.totalAmount}</Typography>
                },
                situationDescription: {
                  index: 6,
                  label: 'Situação',
                  value: <Typography color='textSecondary' variant='subtitle2'>{item?.situationDescription}</Typography>
                },
              }
            })}
            actions={[
              {
                action: rowData => (
                  <Tooltip placement='top' title='Visualizar ordem de venda' enterTouchDelay={0}>
                    <IconButton onClick={() => {
                      setIsSellOrderModalOpen(true);
                      dispatch(loadSellOrder(userId, establishmentId, rowData?.orderId));
                    }}>
                      <FontAwesomeIcon
                        icon={faEye}
                        color="#022A5C"
                        size="xs"
                      />
                    </IconButton>
                  </Tooltip>
                )
              }
            ]}
          />
        )}
        {!isMobile && (
          <MTable
            loading={isLoading}
            headers={headers}
            data={list}
            lineBreak
            actions={[
              rowData => ({
                icon: () => (
                  <FontAwesomeIcon
                    icon={faEye}
                    color="#022A5C"
                    size="xs"
                  />
                ),
                tooltip: 'Visualizar ordem de venda',
                onClick: () => {
                  setIsSellOrderModalOpen(true);
                  dispatch(loadSellOrder(userId, establishmentId, rowData?.orderId));
                }
              })
            ]}
          />
        )}
        <Grid
          container
          xs={12}
          style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}
        >
          <Pagination
            color="primary"
            variant="outlined"
            shape="rounded"
            count={sellOrdersPageCount}
            page={sellOrdersPage}
            onChange={(_, page) => setSellOrdersPage(page)}
          />
        </Grid>
      </Paper>
    </>
  );
}